import { useMemo, useState } from "react";
import ImageGalleryPopUp from "~/components/PopUp/ImageGalleryPopUp";
import { Button } from "~/components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselIndicator,
  CarouselItem,
} from "~/components/ui/carousel";
import { IMAGE_SIZES, buildImageUrl, cn } from "~/lib/utils";
import { LocationPlanBadge } from "../LocationPlanBadge";

interface ImageGridProps {
  groupedImages: Record<
    number,
    {
      name: string | null;
      categoryId: number | null;
      images: Array<{
        id: number | null;
        path: string | null;
        mimetype: string | null;
        alt: string | null;
        size: number | null;
        sort: number | null;
      }>;
    }
  >;
  /** Location plan for the badge (basic, member, premium, superior). Omit or empty to hide badge. */
  plan?: string | null;
  /** Image id that should appear first in header/gallery. */
  primaryImageId?: string | number | null;
}

export const ImageGrid = ({ groupedImages, plan, primaryImageId }: ImageGridProps) => {
  const images = useMemo(() => {
    const flat = Object.values(groupedImages).flatMap((g) => {
    return g.images.map((img) => ({
      ...img,
      path: img.path as string,
      categoryId: g.categoryId,
      category: g.name,
    }));
  });
    if (!primaryImageId) return flat;
    const primaryStr = String(primaryImageId);
    const idx = flat.findIndex(
      (img) => img?.id != null && String(img.id) === primaryStr,
    );
    if (idx <= 0) return flat;
    const primary = flat[idx]!;
    return [primary, ...flat.slice(0, idx), ...flat.slice(idx + 1)];
  }, [groupedImages, primaryImageId]);
  const [open, setOpen] = useState(false);
  const [activeImageId, setActiveImageId] = useState<string | null>(null);

  const openGallery = (imageId?: number | null) => {
    setActiveImageId(imageId != null ? String(imageId) : null);
    setOpen(true);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) setActiveImageId(null);
  };

  return (
    <div className="relative layout-place-full-width">
      <div className="absolute top-3 left-3 z-10">
        <LocationPlanBadge
          plan={plan ?? ""}
          className="size-17 object-contain -rotate-7"
        />
      </div>
      {/* Desktop Grid */}
      <div className="hidden md:grid grid-cols-5 grid-rows-2 gap-3 w-full h-[581px] relative">
        {/* main image */}
        <div
          className={cn(
            "row-span-2 relative",
            images.length === 1 ? "col-span-5" : "",
            images.length > 1 ? "col-span-3" : "",
          )}
        >
          {images[0] && (
            <button
              type="button"
              onClick={() => openGallery(images[0].id)}
              className="w-full h-full block cursor-pointer focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
            >
              <img
                src={buildImageUrl(images[0].path, {
                  size: IMAGE_SIZES.galleryLarge,
                })}
                alt=""
                className="w-full h-full object-cover"
              />
            </button>
          )}
        </div>

        {/* right top */}
        {images[1] && (
          <button
            type="button"
            onClick={() => openGallery(images[1].id)}
            className={cn(
              "w-full h-full block cursor-pointer focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white",
              images.length === 2 ? "col-span-2 row-span-2" : "",
              images.length === 3 ? "col-span-2" : "",
            )}
          >
            <img
                src={buildImageUrl(images[1].path, {
                  size: IMAGE_SIZES.galleryThumb,
                })}
              alt=""
              className="w-full h-full object-cover"
            />
          </button>
        )}
        {images[2] && (
          <button
            type="button"
            onClick={() => openGallery(images[2].id)}
            className={cn(
              "w-full h-full block cursor-pointer focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white",
              images.length === 3 ? "col-span-2" : "",
            )}
          >
            <img
                src={buildImageUrl(images[2].path, {
                  size: IMAGE_SIZES.galleryThumb,
                })}
              alt=""
              className={cn(
                "w-full h-full object-cover",
                images.length === 3 ? "col-span-2" : "",
              )}
            />
          </button>
        )}

        {/* right bottom */}
        {images[3] && (
          <div className="col-span-2 row-span-1">
            <button
              type="button"
              onClick={() => openGallery(images[3].id)}
              className="w-full h-full block cursor-pointer focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
            >
              <img
                src={buildImageUrl(images[3].path, {
                  size: IMAGE_SIZES.galleryThumb,
                })}
                alt=""
                className={cn("w-full h-full object-cover")}
              />
            </button>
          </div>
        )}
        <Button
          variant="outline"
          className="absolute bottom-4 right-4 z-10 border-white text-white"
          onClick={() => openGallery()}
        >
          Alle Bilder
        </Button>
      </div>

      {/* Mobile Carousel */}
      <div className="md:hidden relative">
        <Carousel opts={{ loop: true }}>
          <CarouselContent>
            {images.map((img, index) => (
              <CarouselItem key={img.path + index}>
                <button
                  type="button"
                  onClick={() => openGallery(img.id)}
                  className="w-full h-[400px] block cursor-pointer focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                >
                  <img
                    src={buildImageUrl(img.path, {
                      size: IMAGE_SIZES.galleryLarge,
                    })}
                    alt=""
                    className="w-full h-[400px] object-cover"
                  />
                </button>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselIndicator className="absolute bottom-4 left-1/2 -translate-x-1/2" />
        </Carousel>

        <Button
          variant="outline"
          className="absolute bottom-4 right-4 z-10 border-white text-white"
          onClick={() => openGallery()}
        >
          Alle Bilder
        </Button>
      </div>

      <ImageGalleryPopUp
        open={open}
        onOpenChange={handleOpenChange}
        images={images}
        activeImageId={activeImageId ?? undefined}
        sections={[
          { id: -1, label: "Alle Bilder" },
          ...Object.values(groupedImages)
            .filter((g) => g.categoryId !== null && g.categoryId !== -1)
            .map((g) => ({
              id: g.categoryId!,
              label: g.name!,
            })),
        ]}
      />
    </div>
  );
};
